import {
  Toolbar,
  alpha,
  Checkbox,
  Typography,
  Tooltip,
  IconButton,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import React from "react";
import { AccountHolder } from "../../Account";
import {
  SavedSearchTypeKey,
  SavedSearchTypeToggle,
} from "../../SavedSearchTypeToggle";
import { ResourceListSortType } from "./ListService";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLanguage } from "../../Language";
import DeleteIcon from "@mui/icons-material/Delete";
import { DynamiResourceListView } from "./searches/SavedSearches";

export interface SavedSearchesToolbarProps {
  displayedSearches: DynamiResourceListView[];
  selected: DynamiResourceListView[];
  total: number;
  listType: SavedSearchTypeKey;
  personalCount: number;
  configCount: number;
  changeHandler: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    newValue: SavedSearchTypeKey
  ) => Promise<void>;
  sortType: ResourceListSortType;
  handleSelectAllClick: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  handleSortChange: (event: SelectChangeEvent) => Promise<void>;
  onClickDelete: (
    event: React.MouseEvent<Element, MouseEvent>
  ) => Promise<void>;
}

export const SavedSearchesToolbar: React.FC<SavedSearchesToolbarProps> = ({
  displayedSearches,
  selected,
  total,
  listType,
  personalCount,
  configCount,
  sortType,
  changeHandler,
  handleSelectAllClick,
  handleSortChange,
  onClickDelete,
}) => {
  const isMobile = useIsMobile();
  const { translate } = useLanguage();
  return (
    <>
      {selected.length > 0 ? (
        <Toolbar
          sx={{
            pl: { xs: 0 },
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.activatedOpacity
              ),
          }}
        >
          {/* show checkbox in toolbar when mobile - in table head for desktop */}
          {isMobile && (
            <Checkbox
              color="primary"
              indeterminate={selected.length > 0 && selected.length < total}
              checked={total > 0 && selected.length === total}
              onChange={handleSelectAllClick}
              slotProps={{
                input: {
                  "aria-label": translate("selectAll"),
                },
              }}
            />
          )}
          <Typography
            sx={{ flex: "1 1", textWrap: "nowrap" }}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            {selected.length} {translate("selected")}
          </Typography>
          <Tooltip title={translate("removeSelected")}>
            <IconButton onClick={onClickDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      ) : (
        <Toolbar
          sx={{
            display: "flex",
            flexWrap: "wrap",
            pl: { xs: 0 },
            pb: { xs: "25px" },
            borderBottom: "1px solid var(--mui-palette-TableCell-border)",
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              pl: { xs: "10px" },
              textAlign: "left",
              width: "100%",
            }}
          >
            <Typography variant="h6">
              {!AccountHolder.isConfigAdmin && personalCount + " "}
              {translate("savedSearches") + " "}
            </Typography>
          </Box>
          <Stack
            direction={isMobile ? "column" : "row"}
            sx={{
              gap: 1,
              flex: "1 1",
              alignItems: { xs: "flex-start", sm: "space-between" },
              p: "10px",
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                py: { xs: "10px", sm: 0 },
                pl: { xs: "10px", sm: 0 },
                textAlign: "left",
              }}
            >
              {AccountHolder.isConfigAdmin && (
                <SavedSearchTypeToggle
                  value={listType}
                  personalCount={personalCount}
                  configCount={configCount}
                  onChange={changeHandler}
                ></SavedSearchTypeToggle>
              )}
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                py: { xs: "10px", sm: 0 },
                pl: 0,
                textAlign: "right",
              }}
            >
              {/* show checkbox in toolbar when mobile - in table head for desktop */}
              {isMobile && (
                <Checkbox
                  color="primary"
                  indeterminate={selected.length > 0 && selected.length < total}
                  checked={total > 0 && selected.length === total}
                  onChange={handleSelectAllClick}
                  slotProps={{
                    input: {
                      "aria-label": translate("selectAll"),
                    },
                  }}
                />
              )}

              <FormControl>
                <InputLabel id="list-sort-label">
                  {translate("sortBy")}
                </InputLabel>
                <Select
                  size="small"
                  labelId="list-sort-label"
                  disabled={displayedSearches.length === 0}
                  label={translate("sortBy")}
                  value={sortType}
                  onChange={handleSortChange}
                >
                  <MenuItem value={ResourceListSortType.Name}>
                    {translate("nameSort")}
                  </MenuItem>
                  <MenuItem value={ResourceListSortType.CreationDate}>
                    {translate("creationDateSort")}
                  </MenuItem>
                  <MenuItem value={ResourceListSortType.LastUpdateDate}>
                    {translate("lastUpdateDateSort")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Toolbar>
      )}
    </>
  );
};
